import React, { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import {
  RecyclerListView,
  DataProvider,
  LayoutProvider,
  RecyclerListViewProps,
} from 'recyclerlistview';
import { DNABox, Iffy } from '@alucio/lux-ui';
import { NOTATION_TYPE } from '@alucio/aws-beacon-amplify/src/API';
import { FileType, HubSectionItemStatus } from '@alucio/aws-beacon-amplify/src/models';
import { DocumentVersionORM } from 'src/types/types';
import ThumbnailFooterButton from '../Thumbnail/ThumbnailFooterButton';
import DNASlideRollThumbnail from './DNASlideRollThumbnail';
import { SLIDE_ROLL_VARIANT } from './StyleSetting';
import SlideRollStateProvider, {
  ExtendedState,
  ProviderProps,
  RecyclerListViewDataType,
  useSlideRollState,
} from './SlideRollProvider';
import { EmptyState, HeaderActionButtons, GroupSelection } from './Components';

type DNASlideRollWrapperComponent = React.FC<Omit<ProviderProps, 'children'>> & {
  HeaderActionButtons: typeof HeaderActionButtons,
};

enum ItemType {
  SLIDE,
  SLIDE_WITH_FOOTER_BUTTON,
}

const VISIBLE_TYPE_BADGES: DocumentVersionORM['model']['type'][] = [FileType.WEB, FileType.HTML, FileType.MP4];

const THUMBNAIL_VERTICAL_SPACING = 16;

const styles = StyleSheet.create({
  recyclerListViewStyle: {
    flex: 1,
  },
});

const ThumbnailList: React.FC = () => {
  const {
    presentableData,
    presentable,
    activeSlide,
    activeSlideIndex,
    presentedMeta,
    ignoreSelected,
    horizontal,
    isCustomDeck,
    itemHeight,
    itemWidth,
    itemContainerMinWidth,
    scrollViewRef,
    containerMarginLeft,
    onInsertTextButtonPressed,
  } = useSlideRollState();

  const dataProviderInit = useMemo(() => {
    return new DataProvider((r1: string, r2: string) => {
      return r1 !== r2
    })
  }, [])

  // ROW DATA
  const dataProvider = useMemo(() => dataProviderInit.cloneWithRows(presentableData), [presentableData]);

  const extendedState = useMemo<ExtendedState>(() => {
    return { activeSlide, presentedMeta, notations: presentable.notations ?? [], ignoreSelected };
  }, [activeSlide, presentedMeta, presentable.notations, ignoreSelected]);

  const layoutProvider = useMemo(() => {
    const _layoutProvider = new LayoutProvider((idx) => {
      const showInsertTextBtn = presentableData[idx]?.showInsertTextBtn;
      if (showInsertTextBtn) return ItemType.SLIDE_WITH_FOOTER_BUTTON;
      else return ItemType.SLIDE;
    },
    (type, dim) => {
      switch (type) {
        case ItemType.SLIDE: {
          dim.width = itemContainerMinWidth;
          dim.height = itemHeight + 36 + THUMBNAIL_VERTICAL_SPACING;
          break;
        }
        case ItemType.SLIDE_WITH_FOOTER_BUTTON: {
          dim.width = itemContainerMinWidth;
          dim.height = itemHeight + 36 + 46 + THUMBNAIL_VERTICAL_SPACING;
          break;
        }
      }
    })

    _layoutProvider.shouldRefreshWithAnchoring = false;
    return _layoutProvider;
  }, [itemHeight, itemContainerMinWidth, presentableData])

  const rowRenderer = useCallback<RecyclerListViewProps['rowRenderer']>(
    // @ts-expect-error
    (type, data, _idx, extendedState: ExtendedState) => {
      const { presentablePage, title } = data as RecyclerListViewDataType;
      const { activeSlide, presentedMeta, notations, ignoreSelected } = extendedState;

      const isSelected = !ignoreSelected &&
        activeSlide.presentationPageNumber === presentablePage.presentationPageNumber;
      const presentedSlide = presentedMeta.find(({ pageId }) => pageId === presentablePage.page.pageId);
      const isVisibleTypeBadge = VISIBLE_TYPE_BADGES.includes(presentablePage.documentVersionORM.model.type) &&
        isCustomDeck;
      const hasNotation = !!notations.find(notation => {
        return notation.pageId === presentablePage.page.pageId &&
          notation.status === HubSectionItemStatus.ACTIVE &&
          notation.type === NOTATION_TYPE.CALLOUT;
      });

      switch (type) {
        case ItemType.SLIDE: {
          return (
            <DNABox fill alignX="center">
              <DNASlideRollThumbnail
                key={presentablePage.presentationPageNumber}
                page={presentablePage}
                presentedMeta={presentedSlide}
                isSelected={isSelected}
                tintOverlay={!!presentedMeta.length}
                title={title}
                fileTypeVisible={isVisibleTypeBadge}
                hasNotation={hasNotation}
              />
            </DNABox>
          )
        }
        case ItemType.SLIDE_WITH_FOOTER_BUTTON: {
          return (
            <DNABox fill appearance="col" alignX="center" spacing="sm">
              <DNASlideRollThumbnail
                key={presentablePage.presentationPageNumber}
                page={presentablePage}
                presentedMeta={presentedSlide}
                isSelected={isSelected}
                tintOverlay={!!presentedMeta.length}
                title={title}
                fileTypeVisible={isVisibleTypeBadge}
                hasNotation={hasNotation}
              />
              <DNABox fill style={{ width: itemWidth + 8 }}>
                <ThumbnailFooterButton
                  variant="InsertText"
                  onPress={() => onInsertTextButtonPressed(presentablePage.presentationPageNumber)}
                />
              </DNABox>
            </DNABox>
          )
        }
        default: {
          return null
        }
      }
    },
    [layoutProvider, dataProvider])

  const scrollViewProps = useMemo(() => ({
    contentContainerStyle: {
      marginLeft: containerMarginLeft,
    },
  }), [containerMarginLeft])

  return (
    <RecyclerListView
      // WE NEED KEY HERE TO HELP RERENDER WHEN PRESENTABLE CHANGES, SO THE SCROLLING WORKS CORRECTLY
      key={presentable.id}
      ref={scrollViewRef}
      dataProvider={dataProvider}
      extendedState={extendedState}
      layoutProvider={layoutProvider}
      rowRenderer={rowRenderer}
      style={styles.recyclerListViewStyle}
      canChangeSize
      isHorizontal={horizontal}
      initialRenderIndex={activeSlideIndex}
      scrollViewProps={scrollViewProps}
    />
  )
}

const SlideRoll: React.FC = () => {
  const { variant, handleContainerLayout } = useSlideRollState();

  return (
    <DNABox fill appearance="col" onLayout={handleContainerLayout}>
      <EmptyState />
      <GroupSelection />
      <Iffy is={variant !== SLIDE_ROLL_VARIANT.PREVIEW_MODAL_EMPTY}>
        <ThumbnailList />
      </Iffy>
    </DNABox>
  )
}

const DNASlideRollWrapper: DNASlideRollWrapperComponent = (props) => {
  return (
    <SlideRollStateProvider {...props}>
      <SlideRoll />
    </SlideRollStateProvider>
  )
};

DNASlideRollWrapper.HeaderActionButtons = HeaderActionButtons;

export default DNASlideRollWrapper;
